/**
 * CL-SHIP-02 — checkout-side shipping helpers.
 *
 * Display and request-shaping only. The amount charged is decided server-side
 * in a privileged transition (server/api-util/custom/shippingLineItems.js);
 * nothing here is ever the source of a charged amount.
 */
import { getShippingConfig, normalizeCountryCode } from './shippingRules';

/**
 * The smallest `shippingDetails` that lets the server price the order.
 *
 * The full recipient address only exists once the buyer has filled in name,
 * street and postal code, but shipping has to be re-priced the moment the
 * country is picked. So the speculate call sends the country on its own, in the
 * same place the real order sends the whole address — the server reads exactly
 * one field either way.
 *
 * @param {string|null} countryCode
 * @returns {Object} `{}` or `{ shippingDetails: { address: { country } } }`
 */
export const shippingDestinationDetailsMaybe = countryCode => {
  const country = normalizeCountryCode(countryCode);
  return country ? { shippingDetails: { address: { country } } } : {};
};

/**
 * The delivery countries this listing can actually be shipped to (decision D7).
 *
 * The server rejects a foreign country anyway (FEE_BLOCKED → 400), so this is
 * the second of the two guards D7 asks for, not the only one. Its job is to
 * stop a buyer ever entering an address that cannot be served.
 *
 * A listing saved before CL-SHIP-01 has no origin country and therefore stated
 * no international policy. Restricting its list would fabricate one, so it is
 * left unrestricted — the same ruling the ShippingInfo component follows.
 *
 * @param {Object} publicData - listing publicData
 * @returns {Array<string>|null} ISO codes, or null when every country is allowed
 */
export const allowedDeliveryCountryCodes = publicData => {
  const shippingConfig = getShippingConfig(publicData);
  if (!shippingConfig) return null;

  const originCountry = normalizeCountryCode(shippingConfig.origin?.countryCode);
  if (!originCountry) return null;
  if (shippingConfig.international?.enabled) return null;

  return [originCountry];
};

/**
 * CL-CART-01 — the delivery countries a whole BASKET can be shipped to.
 *
 * The single-item checkout restricts its country select to what the one listing serves (D7). A
 * basket has to satisfy every item at once, so the answer is the intersection: a country only
 * survives if no item refuses it. `null` from `allowedDeliveryCountryCodes` means "unrestricted"
 * and constrains nothing, so those are dropped before intersecting rather than treated as an
 * empty set.
 *
 * An EMPTY array is a real answer, not a bug: two domestic-only listings posted from different
 * countries cannot both reach one address. The caller has to say so rather than offering a
 * country that will be refused when the server prices it.
 *
 * @param {Array<Object>} publicDataList - each item's listing publicData
 * @returns {Array<string>|null} ISO codes every item can reach, or null when none restricts
 */
export const allowedDeliveryCountriesForBasket = publicDataList => {
  const restrictions = (publicDataList || []).map(allowedDeliveryCountryCodes).filter(Boolean);

  if (restrictions.length === 0) {
    return null;
  }

  return restrictions.reduce((shared, list) => shared.filter(code => list.includes(code)));
};
